import { cdtStudioEn } from "src/i18n/locales/en/CdtStudio";
import { pluralOf } from "./plainNames";

type FlagWordsDictionary = typeof cdtStudioEn.flagWords;
type WithoutPlural<K> = K extends `${infer Base}_${"one" | "other"}` ? Base : K;
export type FlagPhraseKey = WithoutPlural<keyof FlagWordsDictionary>;

/** A list of names, read joined by the locale's "and". */
export interface FlagList {
  list: readonly (string | FlagPhrase)[];
}

export type FlagParam = string | number | FlagPhrase | FlagList;

/** One phrase of a finding card, as a `flagWords` key and its params. */
export interface FlagPhrase {
  key: FlagPhraseKey;
  /** Picks the key's `_one` / `_other` form, and fills `{{count}}`. */
  count?: number;
  params?: Readonly<Record<string, FlagParam>>;
}

/** A finding's words, worded in the reader's locale where the card is drawn. */
export interface FlagWords {
  title: FlagPhrase;
  /** Read joined by " · ". */
  detail: readonly FlagPhrase[];
  /** The detail ends with a full stop after its last phrase. */
  stop?: boolean;
  fixLabel?: FlagPhrase;
  /** The card's "As built" and "Want" lines, one phrase per step. */
  evidence?: FlagEvidenceWords;
}

export interface FlagEvidenceWords {
  built?: readonly FlagPhrase[];
  want?: readonly FlagPhrase[];
}

/** A `flagWords` template by its key: the locale's, or the English one. */
export type FlagTemplate = (key: string) => string;

const ENGLISH: Readonly<Record<string, string>> = cdtStudioEn.flagWords;
export const englishFlagTemplate: FlagTemplate = (key) => ENGLISH[key] ?? key;

export const phrase = (
  key: FlagPhraseKey,
  params?: Readonly<Record<string, FlagParam>>,
  count?: number,
): FlagPhrase => ({
  key,
  ...(params ? { params } : {}),
  ...(count === undefined ? {} : { count }),
});

const paramText = (value: FlagParam, template: FlagTemplate): string | number => {
  if (typeof value === "string" || typeof value === "number") return value;
  if ("list" in value)
    return value.list
      .map((item) => (typeof item === "string" ? item : phraseText(item, template)))
      .join(template("and"));
  return phraseText(value, template);
};

export const phraseText = (words: FlagPhrase, template: FlagTemplate): string => {
  const key = words.count === undefined ? words.key : `${words.key}_${pluralOf(words.count)}`;
  const params: Record<string, string | number> =
    words.count === undefined ? {} : { count: words.count };
  for (const [name, value] of Object.entries(words.params ?? {})) {
    params[name] = paramText(value, template);
  }
  return template(key).replace(/\{\{(\w+)\}\}/g, (match, name: string) =>
    name in params ? String(params[name]) : match,
  );
};

export const detailText = (
  words: Pick<FlagWords, "detail" | "stop">,
  template: FlagTemplate,
): string =>
  `${words.detail.map((part) => phraseText(part, template)).join(" · ")}${words.stop ? "." : ""}`;

export const evidenceText = (
  words: FlagEvidenceWords,
  template: FlagTemplate,
): { built?: string[]; want?: string[] } => ({
  ...(words.built ? { built: words.built.map((part) => phraseText(part, template)) } : {}),
  ...(words.want ? { want: words.want.map((part) => phraseText(part, template)) } : {}),
});
